import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly, humanize, money, toIsoDate, todayIso } from '@/lib/utils';
import { DataTable, FilterBar, FilterField, PageHeader, StatTile, Tabs } from '@/components/ui/layout';
import { ReportBuilder } from './ReportBuilder';
import { MortalityReport } from '../ipd/MortalityReport';
import { PackageRevenueReport } from '../billing/PackageRevenueReport';
import { tr } from '@/lib/i18n-core';
import {
  ExpiryLossReport, SalesMarginReport, StockMovementReport, VatReturnReport,
} from '../pharmacy/PharmacyReportsPanel';
import { Alert, Card, CardContent, CardHeader, CardTitle, DatePicker, TableSkeleton } from '@/components/ui/primitives';

interface CensusDay {
  date: string;
  opdVisits: number;
  admissions: number;
  discharges: number;
  newPatients: number;
}

interface ReceivablesReconciliation {
  selfPay: { ageingMinor: number; ledgerMinor: number; differenceMinor: number };
  panel: { ageingMinor: number; ledgerMinor: number; differenceMinor: number };
  customerCredit: {
    heldMinor: number;
    invoiceCount: number;
    ledgerMinor: number;
    differenceMinor: number;
    largest: { invoiceId: string; invoiceNumber: string; customer: string | null; heldMinor: number }[];
  };
}

const TABS_WITH_THEIR_OWN_PERIOD = ['vat', 'sales-margin', 'expiry-loss', 'stock-movement', 'packages'];

/**
 * Standard report library (SRS 10.4).
 *
 * Every report shares one date range so a user can move between them without
 * re-choosing the period, which is how these are actually read — census first,
 * then "why does the revenue not match".
 */
export function ReportsPage() {
  const { can, hasModule } = useAuth();
  // A census counts people who were seen; doctor productivity counts who saw
  // them. A shop and a standalone laboratory do neither.
  const seesPatients = (['opd', 'ipd', 'er'] as const).some((m) => hasModule(m));
  const seesDoctors = (['opd', 'ipd'] as const).some((m) => hasModule(m));
  // The first tab has to be one this facility actually has.
  const [tab, setTab] = useState(seesPatients ? 'census' : hasModule('billing') ? 'revenue' : 'kpis');
  const [from, setFrom] = useState(() => toIsoDate(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(() => todayIso());

  const range = qs({ from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` });

  const census = useQuery({
    queryKey: ['report', 'census', from, to],
    queryFn: () => get<CensusDay[]>(`/reports/daily-census${range}`),
    enabled: tab === 'census',
  });

  const revenue = useQuery({
    queryKey: ['report', 'revenue', from, to],
    queryFn: () => get<{ department: string; grossMinor: number; discountMinor: number; netMinor: number; lineCount: number }[]>(
      `/reports/revenue-by-department${range}`,
    ),
    enabled: tab === 'revenue' && can('report.financial', 'report.read.all'),
  });

  const productivity = useQuery({
    queryKey: ['report', 'productivity', from, to],
    queryFn: () => get<{ doctorId: string; doctor: string; encounters: number; revenueMinor: number; shareMinor: number }[]>(
      `/reports/doctor-productivity${range}`,
    ),
    enabled: tab === 'productivity' && can('report.read.all', 'report.financial'),
  });

  const collections = useQuery({
    queryKey: ['report', 'collections', from, to],
    queryFn: () => get<{ mode: string; count: number; amountMinor: number }[]>(`/reports/collection-summary${range}`),
    enabled: tab === 'collections',
  });

  const receivables = useQuery({
    queryKey: ['report', 'receivables'],
    queryFn: () => get<{ bucket: string; payer: string; amountMinor: number; invoiceCount: number }[]>('/reports/receivables-ageing'),
    enabled: tab === 'receivables' && can('report.financial'),
  });

  const reconciliation = useQuery({
    queryKey: ['report', 'receivables-reconciliation'],
    queryFn: () => get<ReceivablesReconciliation>('/reports/receivables-reconciliation'),
    enabled: tab === 'receivables' && can('report.financial'),
  });

  const diagnoses = useQuery({
    queryKey: ['report', 'diagnoses', from, to],
    queryFn: () => get<{ icdCode: string; icdTitle: string; count: number }[]>(`/reports/top-diagnoses${range}`),
    enabled: tab === 'diagnoses',
  });

  const kpis = useQuery({
    queryKey: ['report', 'kpis', from, to],
    queryFn: () => get<{ labTurnaroundMinutes: number; erWaitMinutes: number; noShowRatePct: number; readmissionRatePct: number }>(
      `/reports/kpis${range}`,
    ),
    enabled: tab === 'kpis',
  });

  return (
    <>
      <PageHeader
        title="Reports"
        description={seesPatients ? 'Census, revenue and the operational indicators' : 'Revenue, collections and what they are made of'}
      />

      <Tabs
        active={tab}
        onChange={setTab}
        // Money is universal; the clinical reports are not. A medical store was
        // offered a daily patient census, a doctor productivity report and an
        // ICD diagnosis breakdown — three reports it can never fill a row of.
        tabs={[
          ...(seesPatients ? [{ id: 'census', label: 'Daily census' }] : []),
          ...(hasModule('billing') ? [{ id: 'revenue', label: 'Revenue' }] : []),
          ...(hasModule('billing') ? [{ id: 'collections', label: 'Collections' }] : []),
          ...(hasModule('billing') ? [{ id: 'receivables', label: 'Receivables' }] : []),
          ...(hasModule('billing') && can('report.financial', 'report.read.all') ? [{ id: 'packages', label: tr('billing', 'package_revenue') }] : []),
          ...(seesDoctors ? [{ id: 'productivity', label: 'Doctors' }] : []),
          ...(seesPatients ? [{ id: 'diagnoses', label: 'Diagnoses' }] : []),
          ...((hasModule('ipd') || hasModule('er')) && can('mortality.read') ? [{ id: 'mortality', label: 'Mortality' }] : []),
          ...(hasModule('pharmacy') && can('report.financial', 'report.read.all', 'pharmacy.report') ? [{ id: 'vat', label: 'VAT return' }] : []),
          ...(hasModule('pharmacy') && can('report.financial', 'report.read.all', 'pharmacy.report') ? [{ id: 'sales-margin', label: 'Sales & margin' }] : []),
          ...(hasModule('pharmacy') ? [{ id: 'expiry-loss', label: 'Expiry loss' }] : []),
          ...(hasModule('pharmacy') ? [{ id: 'stock-movement', label: 'Stock movement' }] : []),
          { id: 'kpis', label: 'KPIs' },
          { id: 'builder', label: 'Builder' },
        ]}
      />

      {!TABS_WITH_THEIR_OWN_PERIOD.includes(tab) && tab !== 'builder' && (
        <FilterBar>
          <FilterField label="From">
            <DatePicker value={from} onChange={setFrom} className="w-40" />
          </FilterField>
          <FilterField label="To">
            <DatePicker value={to} onChange={setTo} className="w-40" />
          </FilterField>
        </FilterBar>
      )}

      {tab === 'census' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activity per day</CardTitle>
          </CardHeader>
          <CardContent>
            {census.isLoading && <Loading />}
            {census.data && (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={census.data.map((d) => ({ ...d, label: dateOnly(d.date) }))}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="opdVisits" name="OPD visits" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="newPatients" name="New patients" stroke="hsl(var(--warning))" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="admissions" name="Admissions" stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'revenue' &&
        (can('report.financial', 'report.read.all') ? (
          <>
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-sm">Net revenue by department</CardTitle>
              </CardHeader>
              <CardContent>
                {revenue.isLoading && <Loading />}
                {revenue.data && revenue.data.length > 0 && (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenue.data.map((r) => ({ ...r, net: r.netMinor / 100 }))}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="department" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v: number) => money(v * 100)} />
                        <Bar dataKey="net" name="Net" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
            <DataTable
              rows={revenue.data}
              loading={revenue.isLoading} refreshing={revenue.isFetching}
              error={revenue.error}
              rowKey={(row) => row.department}
              columns={[
                { header: 'Department', sort: (row) => row.department, cell: (row) => row.department },
                { header: 'Lines', align: 'right', sort: (row) => row.lineCount, cell: (row) => row.lineCount },
                { header: 'Gross', align: 'right', sort: (row) => row.grossMinor, cell: (row) => money(row.grossMinor) },
                { header: 'Discount', align: 'right', sort: (row) => row.discountMinor, cell: (row) => money(row.discountMinor) },
                { header: 'Net', align: 'right', sort: (row) => row.netMinor, cell: (row) => <strong>{money(row.netMinor)}</strong> },
              ]}
            />
          </>
        ) : (
          <Alert>Financial reports are not part of your role.</Alert>
        ))}

      {tab === 'collections' && (
        <DataTable
          rows={collections.data}
          loading={collections.isLoading} refreshing={collections.isFetching}
          error={collections.error}
          rowKey={(row) => row.mode}
          empty="No collections in this period."
          columns={[
            { header: 'Mode', sort: (row) => row.mode, cell: (row) => humanize(row.mode) },
            { header: 'Payments', align: 'right', sort: (row) => row.count, cell: (row) => row.count },
            { header: 'Amount', align: 'right', sort: (row) => row.amountMinor, cell: (row) => money(row.amountMinor) },
          ]}
        />
      )}

      {tab === 'receivables' &&
        (can('report.financial') ? (
          <div className="space-y-4">
            <DataTable
              rows={receivables.data}
              loading={receivables.isLoading} refreshing={receivables.isFetching}
              error={receivables.error}
              rowKey={(row) => `${row.bucket}-${row.payer}`}
              empty="Nothing outstanding."
              columns={[
                { header: 'Age', sort: (row) => row.bucket, cell: (row) => `${row.bucket} days` },
                { header: 'Payer', sort: (row) => row.payer, cell: (row) => row.payer },
                { header: 'Invoices', align: 'right', sort: (row) => row.invoiceCount, cell: (row) => row.invoiceCount },
                {
                  header: 'Outstanding',
                  align: 'right',
                  sort: (row) => row.amountMinor,
                  cell: (row) => <strong className={row.bucket === '90+' ? 'text-destructive' : ''}>{money(row.amountMinor)}</strong>,
                },
              ]}
            />
            {reconciliation.data && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Against the ledger</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 text-sm">
                  <div className="flex justify-between border-b py-1">
                    <span>Patients owing (ageing self-pay)</span>
                    <span className="tabular">{money(reconciliation.data.selfPay.ageingMinor)}</span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span>Receivable — Patients (1300)</span>
                    <span className="tabular">
                      {money(reconciliation.data.selfPay.ledgerMinor)}
                      {reconciliation.data.selfPay.differenceMinor !== 0 && (
                        <span className="ms-2 text-destructive">off by {money(Math.abs(reconciliation.data.selfPay.differenceMinor))}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-b py-1">
                    <span>
                      Credit held for customers ({reconciliation.data.customerCredit.invoiceCount} bill
                      {reconciliation.data.customerCredit.invoiceCount === 1 ? '' : 's'})
                    </span>
                    <span className="tabular font-medium">{money(reconciliation.data.customerCredit.heldMinor)}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>Customer Credit account (2250)</span>
                    <span className="tabular">
                      {money(reconciliation.data.customerCredit.ledgerMinor)}
                      {reconciliation.data.customerCredit.differenceMinor !== 0 && (
                        <span className="ms-2 text-destructive">off by {money(Math.abs(reconciliation.data.customerCredit.differenceMinor))}</span>
                      )}
                    </span>
                  </div>
                  {reconciliation.data.customerCredit.largest.length > 0 && (
                    <div className="pt-2">
                      <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Largest credits held</p>
                      {reconciliation.data.customerCredit.largest.map((row) => (
                        <div key={row.invoiceId} className="flex justify-between py-0.5 text-xs">
                          <span>
                            {row.invoiceNumber} · {row.customer ?? 'Walk-in'}
                          </span>
                          <span className="tabular">{money(row.heldMinor)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Alert>Financial reports are not part of your role.</Alert>
        ))}

      {tab === 'productivity' && (
        <DataTable
          rows={productivity.data}
          loading={productivity.isLoading} refreshing={productivity.isFetching}
          error={productivity.error}
          rowKey={(row) => row.doctorId}
          empty="No doctor activity in this period."
          columns={[
            { header: 'Doctor', sort: (row) => row.doctor, cell: (row) => row.doctor },
            { header: 'Consultations', align: 'right', sort: (row) => row.encounters, cell: (row) => row.encounters },
            { header: 'Revenue generated', align: 'right', sort: (row) => row.revenueMinor, cell: (row) => money(row.revenueMinor) },
            { header: 'Share', align: 'right', sort: (row) => row.shareMinor, cell: (row) => money(row.shareMinor) },
          ]}
        />
      )}

      {tab === 'diagnoses' && (
        <DataTable
          rows={diagnoses.data}
          loading={diagnoses.isLoading} refreshing={diagnoses.isFetching}
          error={diagnoses.error}
          rowKey={(row) => row.icdCode}
          empty="No diagnoses recorded in this period."
          columns={[
            { header: 'ICD', width: '110px', sort: (row) => row.icdCode, cell: (row) => <span className="font-mono text-xs">{row.icdCode}</span> },
            { header: 'Diagnosis', sort: (row) => row.icdTitle, cell: (row) => row.icdTitle },
            { header: 'Cases', align: 'right', sort: (row) => row.count, cell: (row) => row.count },
          ]}
        />
      )}

      {tab === 'kpis' && (
        <>
          {kpis.isLoading && <Loading />}
          {kpis.data && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Lab turnaround"
                value={`${kpis.data.labTurnaroundMinutes} min`}
                sub="Collection to verification"
                tone={kpis.data.labTurnaroundMinutes > 240 ? 'warning' : 'success'}
              />
              <StatTile
                label="ER wait"
                value={`${kpis.data.erWaitMinutes} min`}
                sub="Arrival to doctor"
                tone={kpis.data.erWaitMinutes > 30 ? 'warning' : 'success'}
              />
              <StatTile
                label="No-show rate"
                value={`${kpis.data.noShowRatePct}%`}
                tone={kpis.data.noShowRatePct > 15 ? 'warning' : 'default'}
              />
              <StatTile
                label="30-day readmission"
                value={`${kpis.data.readmissionRatePct}%`}
                tone={kpis.data.readmissionRatePct > 10 ? 'destructive' : 'default'}
              />
            </div>
          )}
        </>
      )}

      {tab === 'mortality' && <MortalityReport from={from} to={to} />}

      {tab === 'vat' && <VatReturnReport />}

      {tab === 'sales-margin' && <SalesMarginReport />}

      {tab === 'expiry-loss' && <ExpiryLossReport />}

      {tab === 'stock-movement' && <StockMovementReport />}

      {tab === 'packages' && <PackageRevenueReport />}

      {tab === 'builder' && <ReportBuilder />}
    </>
  );
}

function Loading() {
  return (
    <TableSkeleton rows={4} />
  );
}
